// Based on https://github.com/opencollective/country-currency-emoji-flags
import cc from 'currency-codes';
import * as FlagJson from '@bbkPricingPage/lib/currencies/currency-flags.json';
// Some references if this needs to be changed later
// https://en.wikipedia.org/wiki/ISO_3166-1
// https://github.com/lukes/ISO-3166-Countries-with-Regional-Codes
// https://github.com/vercel/examples/blob/main/edge-middleware/geolocation/lib/countries.json
// http://country.io/data/
import * as Iso2CountryToCurrencyJson from '@bbkPricingPage/lib/currencies/iso2_country-currency.json';

const getFractionDigits = (amount: number) => {
  return amount % 1 == 0 ? 0 : 2;
};

/** ISO 4217 fraction digits for major currency. */
export const getIso4217CurrencyFractionDigits = (
  currencyCode: string
): number => {
  const code = (currencyCode || 'USD').toUpperCase();
  return cc.code(code)?.digits ?? 2;
};

export const getNameFromCurrency = (currencyCode: string) => {
  return cc.code(currencyCode)?.currency;
};

/**
 * Converts an amount in the smallest currency unit for the given ISO code
 * (e.g. cents for USD, whole yen for JPY) to major units, using ISO 4217 fraction digits.
 */
export const minorUnitsToMajorUnits = (
  amountMinor: number,
  currencyCode: string
): number => {
  return (
    amountMinor / Math.pow(10, getIso4217CurrencyFractionDigits(currencyCode))
  );
};

const getLocale = () => {
  return typeof window !== 'undefined' ? navigator.language : 'en-US';
};

const getFlagFromCurrency = (currencyCode: string) => {
  return FlagJson[currencyCode as keyof typeof FlagJson];
};

export const getListOfCountries = () => {
  return cc
    .countries()
    .sort((a, b) => a.localeCompare(b))
    .filter((country) => !country.startsWith('Zz'));
};

export const getCurrencyCodeFromCountry = (country: string) => {
  return cc.country(country)?.[0]?.code;
};

export const getFormattedNameFromCurrency = (currencyCode: string) => {
  return `${getFlagFromCurrency(currencyCode)} ${getNameFromCurrency(
    currencyCode
  )} (${currencyCode})`;
};

const getFormattedNumber = (amount: number) => {
  return new Intl.NumberFormat(getLocale(), {
    style: 'decimal',
    minimumFractionDigits: getFractionDigits(amount),
    maximumFractionDigits: getFractionDigits(amount),
  }).format(amount);
};

const getFormattedAmount = (
  amount: string | number,
  currency: string,
  fractionDigits?: number
) => {
  if (typeof amount == 'string') {
    amount = Number.parseFloat(amount);
  }

  if (!Number.isFinite(amount)) {
    amount = 0;
  }

  const digits = fractionDigits ?? getFractionDigits(amount);

  return new Intl.NumberFormat(getLocale(), {
    style: 'currency',
    currency: currency,
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    roundingMode: 'halfExpand',
  }).format(amount);
};

export type Iso2CountryCode = keyof typeof Iso2CountryToCurrencyJson;

const getCurrencyFromIso2CountryCode = (iso2currencyCode: Iso2CountryCode) => {
  return Iso2CountryToCurrencyJson[iso2currencyCode];
};

export {
  getCurrencyFromIso2CountryCode,
  getFlagFromCurrency,
  getFormattedAmount,
  getFormattedNumber,
};
